// Firebase 설정 파일 - 초기화된 인스턴스 export
import { initializeApp } from 'firebase/app';
import { getDatabase } from 'firebase/database';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import { getStorage } from 'firebase/storage';

const readEnv = (key, fallback = '') => {
  const value = process.env[key];
  return (value && String(value).trim()) || fallback;
};

const projectId = readEnv('REACT_APP_FIREBASE_PROJECT_ID', 'kuesb-messenger');

const firebaseConfig = {
  apiKey: readEnv('REACT_APP_FIREBASE_API_KEY'),
  authDomain: readEnv('REACT_APP_FIREBASE_AUTH_DOMAIN', `${projectId}.firebaseapp.com`),
  databaseURL: readEnv('REACT_APP_FIREBASE_DATABASE_URL', 'https://kuesb-messenger-default-rtdb.firebaseio.com'),
  projectId,
  storageBucket: readEnv('REACT_APP_FIREBASE_STORAGE_BUCKET', `${projectId}.appspot.com`),
  messagingSenderId: readEnv('REACT_APP_FIREBASE_MESSAGING_SENDER_ID'),
  appId: readEnv('REACT_APP_FIREBASE_APP_ID'),
  measurementId: readEnv('REACT_APP_FIREBASE_MEASUREMENT_ID'),
};

const requiredFirebaseKeys = ['apiKey', 'authDomain', 'projectId', 'appId'];

const missingFirebaseKeys = requiredFirebaseKeys
  .filter((key) => !firebaseConfig[key]);

const missingOptionalKeys = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missingOptionalKeys.length > 0) {
  console.warn('[Firebase] Missing environment variables:', missingOptionalKeys.join(', '));
}

if (missingFirebaseKeys.length > 0) {
  console.error(
    '[Firebase] Missing required env keys:',
    missingFirebaseKeys.join(', '),
    '\nSet REACT_APP_FIREBASE_* keys in .env/.env.development and restart npm start.'
  );
}

const isFirebaseReady = missingFirebaseKeys.length === 0;

const getFirebaseReadyErrorMessage = (featureName = '이 기능') => {
  if (isFirebaseReady) {
    return '';
  }

  const missingKeysText = missingFirebaseKeys.join(', ');
  return `[Firebase] ${featureName}을(를) 사용할 수 없습니다. 누락된 필수 설정: ${missingKeysText}`;
};

const assertFirebaseReady = (featureName = '이 기능') => {
  if (!isFirebaseReady) {
    throw new Error(getFirebaseReadyErrorMessage(featureName));
  }
};

const app = isFirebaseReady ? initializeApp(firebaseConfig) : null;
const database = isFirebaseReady ? getDatabase(app, firebaseConfig.databaseURL) : null;
const firestore = isFirebaseReady ? getFirestore(app) : null;
const auth = isFirebaseReady ? getAuth(app) : null; // Apple/Google 소셜 로그인용 (채팅용 아님)
const storage = isFirebaseReady ? getStorage(app) : null;

let analytics = null;
if (isFirebaseReady && typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app);
  } catch (error) {
    console.warn('[Firebase] Analytics initialization skipped:', error?.message || error);
  }
}

export {
  app,
  database,
  firestore,
  auth,
  analytics,
  storage,
  isFirebaseReady,
  missingFirebaseKeys,
  getFirebaseReadyErrorMessage,
  assertFirebaseReady
};
export default firebaseConfig;

/* 
설정 방법:

1. Firebase 콘솔 (https://console.firebase.google.com/) 접속
2. 프로젝트 설정 → 내 앱 → 웹 앱 설정 복사
3. 위의 값들을 실제 값으로 교체
4. `.env` 또는 `.env.local`에 `REACT_APP_FIREBASE_*` 값을 설정
5. 저장

주의사항:
- databaseURL은 Realtime Database를 생성해야 얻을 수 있습니다
- Firebase 콘솔 → Realtime Database → 데이터베이스 만들기
*/